import type {
  AuthUserDto,
  BootstrapDto,
  CreateDiagramGrantRequest,
  CreateDiagramRequest,
  CreateDiagramShareLinkRequest,
  CreateOrgInviteRequest,
  CreateRoomRequestDto,
  DiagramDto,
  DiagramGrantDto,
  DiagramShareLinkCreatedDto,
  DiagramShareLinkDto,
  DiagramSummaryDto,
  LobbyDiagramDto,
  ExportDto,
  MeDto,
  NodeTypeDto,
  OrgInviteCreatedDto,
  OrgInviteDto,
  OrgMemberDto,
  PortalOverviewDto,
  RenameDiagramRequest,
  RenameExportRequest,
  RoomCapacityDto,
  RoomSnapshotDto,
  RoomSummaryDto,
  ScanTranscriptionResultDto,
  SharedDiagramDto,
  UpdateDisplayNameRequest,
} from '../generated/contracts';

/**
 * The read-only REST surface, plus the three routes that manage the session cookie.
 *
 * Everything that reads room content is read-only — every mutation goes through the hub,
 * so there is deliberately nothing here that writes to a room. Sign-in is not an
 * exception to that rule: it writes a cookie, which a WebSocket cannot do, and no content.
 *
 * Paths are relative: the Vite proxy forwards /api to the backend in development, and in
 * production one origin serves both. Either way the browser sees a single origin, which
 * is what lets the session cookie ride along with the hub's WebSocket unassisted.
 */

/**
 * Thrown when a route needs a session and this browser has none. Not an error case — a 401
 * on an authorized route (CLAUDE.md, "Auth is one policy on the `/api` group") means the
 * cookie is gone or expired, not that anything is broken, and `instanceof NotSignedInError`
 * is the signal a caller is meant to branch on: route to sign-in rather than rendering the
 * failure as a generic error.
 *
 * Every route in this file that can 401 throws this consistently — the seven direct
 * `fetch`-based functions (`getJson`, shared by every simple read; `createRoom`,
 * `getExportText`, `transcribeExport`, `transcribeScan`, `renameExport`, `deleteExport`) all
 * check `response.status === 401` before anything else and throw this rather than falling
 * through to the generic `!response.ok` branch. `login` is the deliberate exception — it
 * returns `null` on 401 instead, because a bad credential is an answer, not a failure, the
 * same way `getSession` turns this exception back into `null` for the identical reason one
 * level up. `logout` does not check its response at all; see its own remarks.
 *
 * As of this writing, **no caller anywhere in the client checks `instanceof
 * NotSignedInError`** — `getSession` catches it and returns `null`, but every portal page
 * and `main.ts` (`getRooms().catch(() => [])`) swallows the rejection into a generic message
 * or an empty list instead of routing to sign-in. That is a known, separate gap on the
 * caller side (main.ts/ui/possession/input/authoring/room/avatars/core are out of this
 * change's scope — `src/client-web/src/net/` only) — this class exists and is thrown
 * consistently so that follow-up work has something real to catch.
 */
export class NotSignedInError extends Error {
  constructor(path: string) {
    super(`${path} requires a signed-in session.`);
    this.name = 'NotSignedInError';
  }
}

/**
 * Thrown when a route answered 403: the caller has a perfectly good session and is not allowed
 * to do this particular thing.
 *
 * **A 403 is a different fact from a 404, and the server draws that line deliberately**
 * (`DiagramAccessGate`, plan/diagram-grants.md): a caller who cannot even *read* a diagram gets
 * the same 404 a nonexistent id gets, because nothing may confirm that an id belongs to somebody
 * else — while a caller who can read but not write gets a 403, because they can already fetch the
 * diagram and a 404 would be a lie told to somebody who cannot be misled by it. The sharing panel
 * is what needs the two apart: a 403 from the share-link list is how it learns it is looking at a
 * read-only caller and takes itself off the page entirely (SPEC-150).
 *
 * **The message is byte-for-byte what the plain `Error` carried before this class existed**, so
 * every pre-existing caller that renders `error.message` — `PortalPeople`, which sees this from
 * `getOrgMembers` for a plain member — reads exactly as it did. Only code that asks
 * `instanceof ForbiddenError` sees any difference.
 */
export class ForbiddenError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ForbiddenError';
  }
}

/**
 * `Results.Problem(...)`'s own `detail` text, when the server sent one — a clean 503 ("no
 * rooms have been seeded" / "no org membership") or a `Results.BadRequest("Name must be 200
 * characters or fewer.")` carries a human-readable reason a caller can render. Best-effort: a
 * response that is not problem+json, or has no body at all, resolves to `null` rather than
 * throwing here — the caller still gets the plain status line either way.
 */
async function problemDetail(response: Response): Promise<string | null> {
  return response
    .clone()
    .json()
    .then((body: unknown) =>
      typeof body === 'object' && body !== null && 'detail' in body
        ? String((body as { detail: unknown }).detail)
        : null,
    )
    .catch(() => null);
}

/**
 * The shared shape every mutation and read in this file follows: same-origin credentials, a
 * 401 becomes {@link NotSignedInError}, and any other non-ok status throws with the server's
 * own `detail` folded in when one was sent. Returns the raw, still-unread `Response` rather
 * than a parsed body — `getExportText` needs `.text()`, `deleteExport` needs nothing at all,
 * and everything else needs `.json()`, so flattening those into one return type would trade
 * the genuine differences this file's callers have for a uniformity that does not fit all of
 * them. `login`'s 401-means-`null` behaviour and `logout`'s "ignore the response entirely"
 * behaviour are both real, deliberate exceptions to the 401-means-throw rule below, so neither
 * goes through this helper — see their own remarks.
 *
 * `errorPrefix` is applied only on the failure path, so every callsite's specific wording
 * ("Creating the room failed", "GET /api/bootstrap failed") is preserved exactly as it read
 * before this helper existed; only the mechanics of getting there are now shared.
 */
async function fetchChecked(path: string, init: RequestInit, errorPrefix: string): Promise<Response> {
  const response = await fetch(path, { ...init, credentials: 'same-origin' });

  if (response.status === 401) {
    throw new NotSignedInError(path);
  }

  if (!response.ok) {
    const detail = await problemDetail(response);
    const message = `${errorPrefix}: ${response.status} ${response.statusText}${detail ? ` — ${detail}` : ''}`;

    // Same message, a narrower type — see {@link ForbiddenError} for why this one status is
    // worth telling apart and why nothing that only reads `.message` can notice.
    throw response.status === 403 ? new ForbiddenError(message) : new Error(message);
  }

  return response;
}

/** A DELETE that answers 204 with no body at all — the one shape neither {@link getJson} nor
 *  {@link postJson} covers, and the same 401/403/detail handling as both. */
async function deleteChecked(path: string, errorPrefix: string): Promise<void> {
  await fetchChecked(path, { method: 'DELETE' }, errorPrefix);
}

/**
 * The mutating counterpart to {@link getJson}, for the handful of administration routes that
 * are REST rather than hub calls — an invite, an archive. Same 401 handling and the same
 * `detail` folding, so a caller branches on {@link NotSignedInError} exactly as it does for a
 * read. A `null` body is a real case (an archive carries no request) rather than an omission.
 */
async function postJson<T>(path: string, body?: unknown): Promise<T> {
  const response = await fetchChecked(
    path,
    {
      method: 'POST',
      headers:
        body === undefined
          ? { accept: 'application/json' }
          : { 'content-type': 'application/json', accept: 'application/json' },
      body: body === undefined ? undefined : JSON.stringify(body),
    },
    `POST ${path} failed`,
  );

  return (await response.json()) as T;
}

async function getJson<T>(path: string): Promise<T> {
  const response = await fetchChecked(path, { headers: { accept: 'application/json' } }, `GET ${path} failed`);
  return (await response.json()) as T;
}

/** The Org, Palace and Room a cold client should load. Never hardcode these. */
export const getBootstrap = () => getJson<BootstrapDto>('/api/bootstrap');

/** Everything needed to render a room from cold. Also the reconnect resync source. */
export const getRoomSnapshot = (roomId: string) =>
  getJson<RoomSnapshotDto>(`/api/rooms/${roomId}`);

/**
 * The rooms the caller's org can reach. v1 seeds exactly one, but this is a list on the
 * wire from day one — callers must not assume there is ever exactly one.
 */
export const getRooms = () => getJson<RoomSummaryDto[]>('/api/rooms');

/**
 * Creates a new room, owned by the caller's own team (`plan/identity-and-portal.md`, this
 * task). Administration, not room content — no lock, no room group — so this is a plain
 * REST mutation, the same "renaming/deleting an export" exception the rest of this file
 * already documents.
 */
export async function createRoom(name: string): Promise<RoomSummaryDto> {
  const body: CreateRoomRequestDto = { name };

  const response = await fetchChecked(
    '/api/rooms',
    {
      method: 'POST',
      headers: { 'content-type': 'application/json', accept: 'application/json' },
      body: JSON.stringify(body),
    },
    'Creating the room failed',
  );

  return (await response.json()) as RoomSummaryDto;
}

/** Who is signed in, plus the org and role that go with it (`plan/identity-and-portal.md`,
 *  the portal dashboard). Distinct from {@link getSession}, which predates org membership
 *  existing at all and stays bare on purpose. */
export const getMe = () => getJson<MeDto>('/api/me');

/**
 * Changes the caller's own display name (`plan/open-items.md`, "Nobody can edit a display
 * name"). Self only — the target is read off the authenticated session server-side, never off
 * this payload, so there is no id or address to pass here. An org owner editing somebody else's
 * name is a different route and a different authorization shape: {@link updateOrgMemberDisplayName}.
 *
 * The live-in-room name is unaffected until the next sign-in — presence is stamped from a claim
 * minted at login, the same trade-off `TunetClaims`'s own remarks already document.
 */
export const updateOwnDisplayName = (displayName: string) =>
  postJson<MeDto>('/api/me/display-name', { displayName } satisfies UpdateDisplayNameRequest);

/**
 * The people in the caller's own org, and when each was last seen — never every user in the
 * system. `lastSeenAt` is `null` for anyone who has never joined a room.
 *
 * **Org owners only, and the refusal is a 403 rather than a narrowed list.** This is the whole
 * org's directory with everyone's activity stamp on it, which is an owner's view of the org —
 * `DashboardEndpoints`' own comment carries the reasoning. Callers should read
 * {@link getPortalOverview}'s `isOwner` first rather than firing this and catching the refusal:
 * not for safety, which is the server's job, but because a page that routinely requests
 * something it will be refused teaches the next reader that the refusal is normal.
 */
export const getOrgMembers = () => getJson<OrgMemberDto[]>('/api/org/members');

/**
 * Removes a member from the org: their `OrgMembership` row is deleted and their account is
 * parked, reversibly, rather than deactivated globally (`plan/portal-design.md`, "Removing a
 * member, and why not a delete"). Nothing is destroyed — the account and everything it authored
 * stay exactly where they were — and the way back is a fresh invite, not a restore control.
 */
export const removeOrgMember = (email: string) =>
  postJson<OrgMemberDto>(`/api/org/members/${encodeURIComponent(email)}/remove`);

/**
 * An org owner renaming somebody else in their own org. Owner only, and the server is what
 * enforces it (403 for a plain member, 404 for an address in another org — the same shape
 * {@link removeOrgMember} already answers). An owner renaming *themselves* goes through
 * {@link updateOwnDisplayName} instead, on the portal's own rail control.
 */
export const updateOrgMemberDisplayName = (email: string, displayName: string) =>
  postJson<OrgMemberDto>(
    `/api/org/members/${encodeURIComponent(email)}/display-name`,
    { displayName } satisfies UpdateDisplayNameRequest,
  );

/**
 * The invites recorded for the caller's own org — pending, accepted and revoked
 * (`plan/auth-and-org-admission.md`, "Correction ten"). Org owners only.
 */
export const getOrgInvites = () => getJson<OrgInviteDto[]>('/api/org/invites');

/**
 * Records an invite for one address. **Sends nothing to anybody**, ever — no SMTP exists and
 * none is planned, which is the owner's own decision (*"Just no email, I'll ping them myself"*).
 * The response's `nextSteps` are the two things the server could not do, served on the wire
 * rather than hardcoded here so the two cannot disagree about what is still required.
 */
export const createOrgInvite = (email: string) =>
  postJson<OrgInviteCreatedDto>('/api/org/invites', { email } satisfies CreateOrgInviteRequest);

/** Withdraws a pending invite. Refused with a 409 for an accepted one, deliberately: the
 *  person is a member now, and an invite is not what grants their access. */
export const revokeOrgInvite = (inviteId: string) =>
  postJson<OrgInviteDto>(`/api/org/invites/${encodeURIComponent(inviteId)}/revoke`);

/**
 * The portal dashboard's rooms and export activity, **scoped to the caller's role on the
 * server** (`plan/portal-design.md`, the portal rebuild).
 *
 * An owner is told when anything last happened in each room, by anyone
 * (`PortalRoomDto.lastActivityAt`); a member is told only when they themselves were last active
 * there, because the other value is never computed into their response rather than being
 * computed and then hidden by this client.
 *
 * A separate route from {@link getRooms} rather than a richer version of it: `/api/rooms` is on
 * the headset's own room-picker path, and it has no business carrying a live presence lookup and
 * a per-room activity aggregate.
 */
export const getPortalOverview = () => getJson<PortalOverviewDto>('/api/portal/overview');

/**
 * The room's exports, newest first (`plan/identity-and-portal.md`, "Exports, as the
 * portal's first real page"). Creating one is a hub mutation — `RoomHub.exportWhiteboard`
 * — this is only ever the read.
 */
export const getExports = (diagramId: string) => getJson<ExportDto[]>(`/api/diagrams/${diagramId}/exports`);

/** Where a given export's own bytes can be downloaded from — a plain link, not a fetch
 *  wrapper: the browser's own download handling (a `<a>` with this as `href`) is simpler
 *  and more correct than re-implementing a save-file dialog over a blob. Also what an
 *  inline `<img>` preview points its `src` at directly — same-origin, so the session
 *  cookie rides along exactly like it does for a `fetch`, with no `crossorigin` needed. */
export const exportDownloadUrl = (diagramId: string, exportId: string) =>
  `/api/diagrams/${diagramId}/exports/${exportId}/download`;

/**
 * The raw text of a text-shaped export (Mermaid source today — an SVG export is shown via
 * an `<img src>` pointed straight at {@link exportDownloadUrl} instead, since that needs no
 * fetch at all). Same download route the `<img>` preview and the download link both already
 * use; this just reads the body as text rather than letting the browser render it as an
 * image. Same-origin, so the session cookie rides along unassisted, the same reasoning
 * {@link exportDownloadUrl}'s own doc comment gives.
 */
export async function getExportText(diagramId: string, exportId: string): Promise<string> {
  const url = exportDownloadUrl(diagramId, exportId);
  const response = await fetchChecked(url, { headers: { accept: 'text/plain' } }, `GET ${url} failed`);
  return response.text();
}

/**
 * Board OCR (plan/... "reading handwriting off an export"): posts a rasterised PNG of the
 * export — the portal's own `<canvas>` render of the same SVG the download route serves,
 * never a second fetch of the SVG itself — and gets back the export with `transcribedText`/
 * `transcribedAt` set. A REST mutation, the same "administration of a stored artifact, not
 * room content" reasoning `renameExport`/`deleteExport` already document: an export has no
 * lock and no live room group, and this stores a result on it exactly like a rename does.
 * Not wrapped in `getJson`/a shared POST helper — the body is raw image bytes, not JSON.
 */
export async function transcribeExport(diagramId: string, exportId: string, image: Blob): Promise<ExportDto> {
  const path = `/api/diagrams/${diagramId}/exports/${exportId}/transcribe`;

  const response = await fetchChecked(
    path,
    {
      method: 'POST',
      headers: { 'content-type': image.type || 'image/png', accept: 'application/json' },
      body: image,
    },
    "Reading the export's handwriting failed",
  );

  return (await response.json()) as ExportDto;
}

/**
 * The scan tool's own transcribe route (`ScanEndpoints.cs`) — reads the handwriting off a
 * client-rasterised image of a swept selection, with nothing stored server-side. A sibling of
 * {@link transcribeExport}, not a call to it: a scan has no stored `Export` row to attach a
 * result to (see that route's own remarks for why), so this posts to a different path and gets
 * back a bare `{ text }` rather than a whole `ExportDto`. `text` is `null` for a genuinely empty
 * transcription — the caller (`ScanTool.ts`) must treat `null`/blank exactly like a failed call:
 * delete nothing, draw nothing back.
 */
export async function transcribeScan(roomId: string, image: Blob): Promise<ScanTranscriptionResultDto> {
  const path = `/api/rooms/${roomId}/scan/transcribe`;

  const response = await fetchChecked(
    path,
    {
      method: 'POST',
      headers: { 'content-type': image.type || 'image/png', accept: 'application/json' },
      body: image,
    },
    'Reading the scanned handwriting failed',
  );

  return (await response.json()) as ScanTranscriptionResultDto;
}

/**
 * Renaming and deleting an export are administration, not room content
 * (`plan/identity-and-portal.md`, "The REST boundary bends, deliberately") — an export has
 * no lock and no live room group to broadcast into — so both are plain REST mutations, the
 * one deliberate exception to "REST is read-only" this file otherwise follows.
 */
export async function renameExport(diagramId: string, exportId: string, name: string): Promise<ExportDto> {
  const body: RenameExportRequest = { name };
  const path = `/api/diagrams/${diagramId}/exports/${exportId}`;

  // Previously threw a bare status line, discarding `Results.BadRequest("Name must be 200
  // characters or fewer.")`'s own detail entirely — going through fetchChecked fixes that as
  // a side effect of unifying the error path, not as a separate change.
  const response = await fetchChecked(
    path,
    {
      method: 'PATCH',
      headers: { 'content-type': 'application/json', accept: 'application/json' },
      body: JSON.stringify(body),
    },
    'Renaming the export failed',
  );

  return (await response.json()) as ExportDto;
}

/** No confirmation of its own — the caller (`PortalExports.ts`) is where the deliberate
 *  step belongs, once, at the point of the human's own decision (CLAUDE.md's own reasoning
 *  for why a hub method never re-implements a caller's UI-level guard applies here too). */
export async function deleteExport(diagramId: string, exportId: string): Promise<void> {
  const path = `/api/diagrams/${diagramId}/exports/${exportId}`;

  // Same fix as renameExport above: this used to discard the server's own `detail` entirely.
  await fetchChecked(path, { method: 'DELETE' }, 'Deleting the export failed');
}

/**
 * Creates a new, empty diagram owned by the caller's own org (`plan/room-to-diagram.md`, task
 * R1). Administration, not room content — no lock, no room group — the same REST-mutation shape
 * {@link createRoom} already used, but a diagram needs no team: SPEC-132 says the org owns it.
 */
export const createDiagram = (name: string) =>
  postJson<DiagramDto>('/api/diagrams', { name } satisfies CreateDiagramRequest);

/**
 * Renames a diagram. Write access, the same bar {@link createDiagramGrant} and its siblings
 * already require — every member of the diagram's own org clears it with no grant at all
 * (SPEC-144) — so this needs no confirmation of its own; typing the old name back undoes it.
 */
export async function renameDiagram(diagramId: string, name: string): Promise<DiagramDto> {
  const body: RenameDiagramRequest = { name };

  const response = await fetchChecked(
    `/api/diagrams/${encodeURIComponent(diagramId)}`,
    {
      method: 'PATCH',
      headers: { 'content-type': 'application/json', accept: 'application/json' },
      body: JSON.stringify(body),
    },
    'Renaming the diagram failed',
  );

  return (await response.json()) as DiagramDto;
}

/**
 * The interim "Enter VR" seam (`plan/room-to-diagram.md`, task R1): the room already showing
 * this diagram, or a freshly made one — never one the caller is asked to name or pick, which is
 * what SPEC-153 actually forbids. Retired once R2 makes a room spawn on entry instead of a
 * pointer this client has to resolve first.
 */
export const enterDiagramRoom = (diagramId: string) =>
  postJson<RoomSummaryDto>(`/api/diagrams/${encodeURIComponent(diagramId)}/room`);

/**
 * A diagram, bounded, for reading rather than editing — what the desktop viewer renders
 * (plan/diagram-extraction.md, A5). Positions, shapes and colours, capped; never the room
 * snapshot, whose stroke set is unbounded and whose per-node version and lock metadata exist
 * only to support a mutation this page cannot perform.
 *
 * **Keyed by diagram, never by room.** The diagram is what is owned and shared (SPEC-132), and
 * the viewer has no room at all — no hub connection, no presence, no session in the room sense.
 * The one thing it still needs a session for is authorization: SPEC-136 opens this link for
 * anyone signed in to the owning org and refuses everyone else, so a 401 here is
 * {@link NotSignedInError} exactly as it is everywhere else in this file.
 */
export const getDiagramSummary = (diagramId: string) =>
  getJson<DiagramSummaryDto>(`/api/diagrams/${encodeURIComponent(diagramId)}`);

/**
 * Every diagram the caller can reach, ordered for the lobby: occupied first, then by most recent
 * activity (SPEC-157).
 *
 * **Flat on purpose.** A row carries a name, an occupant count, a last-active time and a node
 * count — no nodes, no connections, no geometry. The bounded summary above is what draws a
 * miniature, and it is fetched per diagram only for the few the lobby is actually rendering:
 * arriving in a lobby with three hundred diagrams has to be one small response, not three hundred
 * bounded payloads.
 */
export const getLobbyDiagrams = () => getJson<LobbyDiagramDto[]>('/api/diagrams/lobby');

/**
 * Spawns the room a session begins in: a fresh one, showing no diagram (SPEC-161).
 *
 * Deliberately not "find me an empty room" — reusing whichever empty room happened to exist is
 * how somebody would arrive standing in a stranger's session, which is precisely what SPEC-153
 * abolished when it stopped anybody picking a room.
 */
export const enterLobbyRoom = () => postJson<RoomSummaryDto>('/api/rooms/lobby', {});

/**
 * Where a diagram's own export can be downloaded from — a plain link, not a fetch wrapper, the
 * same reasoning {@link exportDownloadUrl} gives for the room-keyed route. This is a second key
 * onto the same stored bytes, not a second export: the Mermaid source and the whiteboard image
 * were both produced server-side long before the viewer existed (SPEC-137 says reuse them).
 */
export const diagramExportDownloadUrl = (diagramId: string, exportId: string) =>
  `/api/diagrams/${encodeURIComponent(diagramId)}/exports/${encodeURIComponent(exportId)}/download`;

/** The raw text of a diagram's text-shaped export (Mermaid source) — the diagram-keyed sibling
 *  of {@link getExportText}, and the same download route the link already points at. */
export async function getDiagramExportText(diagramId: string, exportId: string): Promise<string> {
  const url = diagramExportDownloadUrl(diagramId, exportId);
  const response = await fetchChecked(url, { headers: { accept: 'text/plain' } }, `GET ${url} failed`);
  return response.text();
}

/**
 * ============================================================================
 * SHARING: the six management routes, plus the one anonymous read.
 * ============================================================================
 *
 * plan/diagram-grants.md, "The management surface". All six management routes are gated
 * identically on **write** access to the diagram (SPEC-150) and answer two different refusals
 * deliberately: 404 for a caller who cannot read it at all — indistinguishable from an id that
 * names nothing, so nothing confirms that a diagram belongs to somebody else — and 403 for one
 * who can read but not write, which is {@link ForbiddenError} here and is how the sharing panel
 * learns to take itself off the page.
 *
 * **A grant only ever ADDS access (SPEC-144).** Every member of the owning org already reads and
 * writes with no grant, so nothing in this file is a way to restrict somebody; there is no
 * "revoke a member's access" call because there is no such operation.
 */

/** Where a share token's page lives, site-relative — the client-side mirror of
 *  `SharedDiagramRoutes.PathFor`. Issuing hands the path back on the response
 *  (`DiagramShareLinkCreatedDto.path`) so nothing has to assemble one; this exists for the page
 *  that has to recognise its own URL on the way in. */
export const SHARED_DIAGRAM_PAGE_PREFIX = '/s';

/**
 * Every live share link on a diagram — **never their tokens** (SPEC-148). A token is disclosed
 * exactly once, in {@link createDiagramShareLink}'s response, and is not recoverable afterwards,
 * which is why every row carries an issuer-supplied `label`: a list of tokenless rows would
 * otherwise be unidentifiable.
 */
export const getDiagramShareLinks = (diagramId: string) =>
  getJson<DiagramShareLinkDto[]>(`/api/diagrams/${encodeURIComponent(diagramId)}/share-links`);

/**
 * Issues a share link. **The `token` and `path` on the response are the only time either is ever
 * disclosed** — only `SHA256(token)` is stored, so a caller that discards this response has
 * nothing to do but revoke the link and issue another (plan/diagram-grants.md, "Storing the hash,
 * and the trade that was actually accepted"). Any UI calling this owes the person a copy control
 * they cannot walk past.
 */
export const createDiagramShareLink = (diagramId: string, label: string | null) =>
  postJson<DiagramShareLinkCreatedDto>(
    `/api/diagrams/${encodeURIComponent(diagramId)}/share-links`,
    { label, expiresAt: null } satisfies CreateDiagramShareLinkRequest,
  );

/** Revokes a share link by deleting its row, so a revoked token and a token that never existed
 *  are the same absent row. Someone with the page already open keeps seeing what they loaded —
 *  the shared read is a one-shot fetch with no live connection — and is refused on next load. */
export const revokeDiagramShareLink = (diagramId: string, linkId: string) =>
  deleteChecked(
    `/api/diagrams/${encodeURIComponent(diagramId)}/share-links/${encodeURIComponent(linkId)}`,
    'Revoking the share link failed',
  );

/** Everybody granted access to this diagram *beyond* what their org membership already gives. */
export const getDiagramGrants = (diagramId: string) =>
  getJson<DiagramGrantDto[]>(`/api/diagrams/${encodeURIComponent(diagramId)}/grants`);

/**
 * Grants one person or one team a level on this diagram. Exactly one of `userId`/`teamId`, which
 * the database `CHECK` constraint also says; a second grant on the same subject is a **409**
 * rather than an in-place level change, so changing a level is a revoke and a reissue.
 */
export const createDiagramGrant = (diagramId: string, request: CreateDiagramGrantRequest) =>
  postJson<DiagramGrantDto>(`/api/diagrams/${encodeURIComponent(diagramId)}/grants`, request);

/** Removes a grant. Takes effect on the granted person's very next request — a session already
 *  inside a room keeps the level it resolved at `JoinRoom` until they rejoin, which is precisely
 *  the property org membership already has on that hub. */
export const revokeDiagramGrant = (diagramId: string, grantId: string) =>
  deleteChecked(
    `/api/diagrams/${encodeURIComponent(diagramId)}/grants/${encodeURIComponent(grantId)}`,
    'Removing the grant failed',
  );

/**
 * What a share link opens: one diagram's drawable content, for a caller who is nobody.
 *
 * **`null` is the only failure, and that is the security property rather than convenience.** A
 * malformed token, a token nobody ever minted, a revoked link and an expired one all answer the
 * same empty 404 (`ShareTokens.ResolveSharedDiagramAsync`), so a holder of a wrong token cannot
 * tell it apart from a right token that was switched off. Turning all four into one `null` here
 * is what keeps the page from inventing a distinction the server refused to make.
 *
 * **Deliberately not routed through {@link fetchChecked}.** That helper turns a 401 into
 * {@link NotSignedInError}, which every portal page treats as "go and sign in" — exactly the
 * wrong response on the one route in this file that has no session and needs none. This route
 * lives outside the authorized `/api` group entirely (`SharedDiagramEndpoints`) and cannot
 * answer 401, so the translation would be dead code that misleads the next reader about what
 * this page is.
 */
export async function getSharedDiagram(token: string): Promise<SharedDiagramDto | null> {
  const path = `/api/shared/${encodeURIComponent(token)}`;
  const response = await fetch(path, {
    headers: { accept: 'application/json' },

    // Same-origin rather than omitted: a signed-in person opening their own share link should
    // not have their cookie stripped by this client, and the server does not read one here
    // either way. Omitting it would be a claim about the route that this file cannot enforce.
    credentials: 'same-origin',
  });

  if (response.status === 404) {
    return null;
  }

  if (!response.ok) {
    throw new Error(`GET ${path} failed: ${response.status} ${response.statusText}`);
  }

  return (await response.json()) as SharedDiagramDto;
}

/**
 * The node catalogue: every shape type a room offers, grouped by category.
 *
 * Fetched rather than hardcoded so the tray and the server can never disagree about what is
 * placeable — and so moving the catalogue into the database later changes nothing here.
 */
export const getNodeTypes = () => getJson<NodeTypeDto[]>('/api/node-types');

/**
 * The node cap a room enforces, and the warning margin before it — plan/open-items.md, "A
 * hard cap of 100 nodes per room". Fetched rather than hardcoded for the identical reason
 * `getNodeTypes` is: the tray and `RoomMutationService.CreateNodeAsync` must not be able to
 * disagree about where the limit sits.
 */
export const getRoomCapacity = () => getJson<RoomCapacityDto>('/api/room-capacity');

/**
 * Who this browser is signed in as, or null if it is not.
 *
 * A 401 here is an answer, not a failure: the cookie is HttpOnly, so asking the server is
 * the only way to find out whether the one already in the jar is still good.
 */
export async function getSession(): Promise<AuthUserDto | null> {
  try {
    return await getJson<AuthUserDto>('/api/auth/session');
  } catch (error) {
    if (error instanceof NotSignedInError) {
      return null;
    }

    throw error;
  }
}

/**
 * Signs in, returning the account. Resolves with null when the credentials are refused —
 * the server does not say which half was wrong, and neither should this.
 */
export async function login(email: string, password: string): Promise<AuthUserDto | null> {
  const response = await fetch('/api/auth/login', {
    method: 'POST',
    headers: { 'content-type': 'application/json', accept: 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify({ email, password }),
  });

  if (response.status === 401) {
    return null;
  }

  if (!response.ok) {
    throw new Error(`Sign-in failed: ${response.status} ${response.statusText}`);
  }

  return (await response.json()) as AuthUserDto;
}

/**
 * Clears the session cookie. Deliberately does not check `response.status` at all, including
 * 401 — `/api/auth/logout` sits inside the authorized `/api` group like every other route
 * (CLAUDE.md), so a cookie that is already gone or expired 401s here too, but that is not a
 * failure worth surfacing: the caller's goal ("no session") is already true either way, and
 * throwing `NotSignedInError` from the one route whose entire purpose is ending a session
 * would be a strange thing to route to a sign-in screen.
 */
export async function logout(): Promise<void> {
  await fetch('/api/auth/logout', { method: 'POST', credentials: 'same-origin' });
}
